/**
 * Coque de la console d'exploitation.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ELLE NE RESSEMBLE PAS AU BACK-OFFICE, ET C'EST VOULU
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le back-office d'un théâtre est clair, aux couleurs du client
 * (personnalisation PER-01…07). La console est **sombre**, neutre, sans logo de
 * personne. Ce n'est pas un goût : c'est un signal. Quelqu'un qui a huit
 * onglets ouverts doit savoir, en un dixième de seconde et sans lire, s'il
 * regarde l'outil de l'éditeur ou celui d'un client. Deux interfaces qui se
 * ressemblent finissent par se confondre, et la confusion se paie en gestes
 * faits dans la mauvaise fenêtre.
 *
 * Le bandeau de bascule, lui, est clair et ambre : il tranche sur la coque
 * sombre, donc il ne peut pas passer inaperçu.
 */

import { useEffect } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router'

import { cn } from '../../lib/cn'
import { Button, ChargementPage, useToast } from '../../ui'
import { BandeauBascule } from './BandeauBascule'
import { CHEMINS_CONSOLE } from './chemins'
import { useBascule, useDeconnexion, useSessionConsole } from './api'

const ENTREES = [
  { chemin: CHEMINS_CONSOLE.racine, libelle: 'Vue d’ensemble', exact: true },
  { chemin: CHEMINS_CONSOLE.tenants, libelle: 'Théâtres', exact: false },
  { chemin: CHEMINS_CONSOLE.moyensPaiement, libelle: 'Paiements', exact: false },
  { chemin: CHEMINS_CONSOLE.sante, libelle: 'Santé', exact: false },
  /* ★ « Sauvegardes » est une entrée de PREMIER niveau, pas un onglet caché
     dans la fiche d'un théâtre. La question qu'elle répond — « y a-t-il un
     théâtre dont la dernière sauvegarde a échoué ? » — se pose sur le PARC,
     jamais sur un client en particulier : rangée sous une fiche, on ne la
     poserait qu'après avoir déjà eu un soupçon. */
  { chemin: CHEMINS_CONSOLE.sauvegardes, libelle: 'Sauvegardes', exact: false },
  { chemin: CHEMINS_CONSOLE.statistiques, libelle: 'Statistiques', exact: false },
  { chemin: CHEMINS_CONSOLE.journal, libelle: 'Journal', exact: false },
  { chemin: CHEMINS_CONSOLE.superadmins, libelle: 'Comptes', exact: false },
  { chemin: CHEMINS_CONSOLE.securite, libelle: 'Sécurité', exact: false },
] as const

export function LayoutConsole() {
  const session = useSessionConsole()
  const bascule = useBascule()
  const deconnexion = useDeconnexion()
  const naviguer = useNavigate()
  const toast = useToast()

  /**
   * ★ Le titre de l'onglet dit « Console », toujours.
   *
   * Sans cela, il porterait le nom d'un THÉÂTRE : le fournisseur d'apparence
   * (PER-07) est monté au-dessus du routeur et écrit `document.title` depuis
   * `GET /apparence/publique`. En production, cette route répond 404 sur l'hôte
   * de la console (portée `tenant`) et le problème ne se pose pas — mais en
   * développement, où une seule API sert tout, l'onglet de la console
   * s'annonçait comme celui d'un client. C'est exactement la confusion que la
   * coque sombre cherche à empêcher : on ne la laisse pas rentrer par le titre.
   */
  useEffect(() => {
    document.title = 'Console — exploitation de la plateforme'
  }, [])

  if (session.isLoading) return <ChargementPage />

  async function seDeconnecter(): Promise<void> {
    try {
      await deconnexion.mutateAsync()
      await naviguer(CHEMINS_CONSOLE.connexion, { replace: true })
    } catch (erreur) {
      toast.erreur(erreur)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-100">
      {/* ★ Le bandeau AVANT tout le reste, et il pousse le contenu : un bandeau
          flottant se ferait recouvrir par la première modale venue. */}
      {bascule.data != null && <BandeauBascule bascule={bascule.data} />}

      <header className="bg-slate-900 text-slate-100">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-semibold tracking-tight">Console</span>
            <span className="text-xs text-slate-400">exploitation de la plateforme</span>
          </div>

          <nav className="flex flex-1 flex-wrap gap-1" aria-label="Console">
            {ENTREES.map((entree) => (
              <NavLink
                key={entree.chemin}
                to={entree.chemin}
                end={entree.exact}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                  )
                }
              >
                {entree.libelle}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-300">{session.data?.superadmin.email}</span>
            <Button
              variante="secondaire"
              taille="sm"
              chargement={deconnexion.isPending}
              onClick={() => {
                void seDeconnecter()
              }}
            >
              Se déconnecter
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <footer className="mx-auto w-full max-w-7xl px-4 pb-6 text-xs text-slate-500">
        Les gestes de cette console sont journalisés dans <code>audit_plateforme</code>. Une bascule
        vers un théâtre l’est <strong>en plus</strong> dans le journal de ce théâtre, que son
        administrateur peut lire et que personne ne peut réécrire.
      </footer>
    </div>
  )
}
